import React, { useEffect, useState } from 'react'
import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { trasladosService } from '../../../api/trasladosService'
import { useAuth } from '../../../contexts/AuthContext'
import type { Traslado } from '../../../types/traslados'
import { FLEXY_PRODUCT_TABLE_WAREHOUSES } from '../../../utils/productTableBodegasConfig'
import {
  formatTrasladoFecha,
  getTrasladoAlmacenLabel
} from '../../../utils/trasladosMapper'
import ConfirmationModal from '../../modals/ConfirmationModal'
import Button from '../../ui/Button/Button'
import { useTrasladoActions } from '../hooks/useTraslados'
import {
  formatTrasladoEstado,
  getTrasladoEstadoBadgeClass,
  isTrasladoErrorEstado
} from './trasladoValidation'

interface FlexyTrasladoDetailModalProps {
  trasladoId: number | null
  isOpen: boolean
  onClose: () => void
  onUpdated: () => void
}

type PendingAction = 'reenviar' | null

function DetailField({
  label,
  value
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p
        className="truncate text-xs text-gray-900 sm:text-sm"
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </p>
    </div>
  )
}

const FlexyTrasladoDetailModal: React.FC<FlexyTrasladoDetailModalProps> = ({
  trasladoId,
  isOpen,
  onClose,
  onUpdated
}) => {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('manage_wms_traslados')
  const [traslado, setTraslado] = useState<Traslado | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const { actionLoading, reenviarFlexy } = useTrasladoActions(() => {
    onUpdated()
    if (trasladoId) loadDetail(trasladoId)
  })

  const loadDetail = async (id: number) => {
    setIsLoading(true)
    try {
      const data = await trasladosService.getTrasladoById(id)
      setTraslado(data)
    } catch {
      setTraslado(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && trasladoId) {
      loadDetail(trasladoId)
    } else {
      setTraslado(null)
      setPendingAction(null)
    }
  }, [isOpen, trasladoId])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pendingAction === null) onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose, pendingAction])

  if (!isOpen) return null

  const handleConfirm = async () => {
    if (!trasladoId || pendingAction !== 'reenviar') return
    await reenviarFlexy(trasladoId, false)
    setPendingAction(null)
  }

  const canReintentar = canManage && isTrasladoErrorEstado(traslado?.estado ?? '')
  const origenLabel = traslado
    ? getTrasladoAlmacenLabel(
        traslado.idalmacen_origen,
        traslado.nombre_almacen_origen,
        traslado.codigo_almacen_origen,
        FLEXY_PRODUCT_TABLE_WAREHOUSES
      )
    : '—'
  const destinoLabel = traslado
    ? getTrasladoAlmacenLabel(
        traslado.idalmacen_destino,
        traslado.nombre_almacen_destino,
        traslado.codigo_almacen_destino,
        FLEXY_PRODUCT_TABLE_WAREHOUSES
      )
    : '—'
  const lineasCount = traslado?.lineas?.length ?? 0

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900/40 p-2 sm:p-3"
        onClick={(event) => {
          if (event.target === event.currentTarget && pendingAction === null) onClose()
        }}
      >
        <div
          className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="traslado-detail-title"
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 sm:px-4">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3
                id="traslado-detail-title"
                className="truncate text-base font-semibold text-gray-900"
              >
                {traslado?.consecutivo ?? (traslado ? `#${traslado.id}` : '…')}
              </h3>
              {traslado && (
                <span
                  className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${getTrasladoEstadoBadgeClass(traslado.estado)}`}
                >
                  {formatTrasladoEstado(traslado.estado)}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Cerrar detalle"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-2.5 sm:px-4">
            {isLoading ? (
              <div className="space-y-2" aria-busy="true" aria-label="Cargando detalle">
                <div className="h-10 animate-pulse rounded bg-gray-100" />
                <div className="grid grid-cols-3 gap-2">
                  <div className="h-9 animate-pulse rounded bg-gray-100" />
                  <div className="h-9 animate-pulse rounded bg-gray-100" />
                  <div className="h-9 animate-pulse rounded bg-gray-100" />
                </div>
                <div className="h-20 animate-pulse rounded bg-gray-100" />
              </div>
            ) : traslado ? (
              <div className="space-y-2.5">
                <section className="rounded-md border border-gray-100 bg-gray-50/80 px-2.5 py-2">
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        Origen
                      </p>
                      <p className="truncate text-xs font-medium text-gray-900 sm:text-sm" title={origenLabel}>
                        {origenLabel}
                      </p>
                    </div>
                    <ArrowsRightLeftIcon className="h-4 w-4 shrink-0 text-custom-blue" aria-hidden="true" />
                    <div className="min-w-0 flex-1 text-right">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        Destino
                      </p>
                      <p className="truncate text-xs font-medium text-gray-900 sm:text-sm" title={destinoLabel}>
                        {destinoLabel}
                      </p>
                    </div>
                  </div>
                </section>

                <section className="grid grid-cols-2 gap-x-3 gap-y-2 rounded-md border border-gray-100 px-2.5 py-2 sm:grid-cols-3">
                  <DetailField
                    label="ID Pedido"
                    value={
                      traslado.id_pedido != null && String(traslado.id_pedido).trim() !== ''
                        ? String(traslado.id_pedido)
                        : '—'
                    }
                  />
                  <DetailField
                    label="ID Order"
                    value={
                      traslado.idorder != null && String(traslado.idorder).trim() !== ''
                        ? String(traslado.idorder)
                        : '—'
                    }
                  />
                  <DetailField label="ID interno" value={String(traslado.id)} />
                  <DetailField label="ID Flexy" value={traslado.flexy_idtransferencia || '—'} />
                  <DetailField label="Creación" value={formatTrasladoFecha(traslado.fecha_creacion)} />
                  <DetailField label="Vencimiento" value={formatTrasladoFecha(traslado.fecha_vencimiento)} />
                  {traslado.fecha_aprobacion && (
                    <DetailField
                      label="Aprobación"
                      value={formatTrasladoFecha(traslado.fecha_aprobacion)}
                    />
                  )}
                  {traslado.usuario_creador && (
                    <DetailField label="Creado por" value={traslado.usuario_creador.nombre} />
                  )}
                </section>

                <section className="rounded-md border border-gray-100 px-2.5 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Glosa</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-gray-700 sm:text-sm" title={traslado.glosa}>
                    {traslado.glosa?.trim() ? traslado.glosa : '—'}
                  </p>
                </section>

                <section>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Productos
                    </p>
                    <span className="text-[10px] font-medium text-gray-500">
                      {lineasCount} {lineasCount === 1 ? 'línea' : 'líneas'}
                    </span>
                  </div>
                  <div className="overflow-hidden rounded-md border border-gray-100">
                    <div className="max-h-36 overflow-auto">
                      <table className="min-w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50">
                          <tr className="border-b border-gray-100 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                            <th className="px-2 py-1.5">SKU</th>
                            <th className="px-2 py-1.5">Producto</th>
                            <th className="px-2 py-1.5 text-right">Cant.</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {lineasCount === 0 ? (
                            <tr>
                              <td colSpan={3} className="px-2 py-3 text-center text-gray-500">
                                Sin líneas
                              </td>
                            </tr>
                          ) : (
                            traslado.lineas.map((linea) => (
                              <tr key={`${traslado.id}-${linea.sku}-${linea.id ?? ''}`}>
                                <td className="whitespace-nowrap px-2 py-1 font-medium text-gray-900">
                                  {linea.sku}
                                </td>
                                <td className="max-w-[10rem] truncate px-2 py-1 text-gray-700" title={linea.producto}>
                                  {linea.producto}
                                </td>
                                <td className="whitespace-nowrap px-2 py-1 text-right text-gray-900">
                                  {linea.cantidad}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
              </div>
            ) : (
              <div className="rounded-md border border-red-100 bg-red-50 px-3 py-4 text-center">
                <p className="text-sm font-medium text-red-700">
                  No se pudo cargar el detalle.
                </p>
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 px-3 py-2 sm:px-4">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              Cerrar
            </Button>
            {canReintentar && (
              <Button
                type="button"
                variant="warning"
                size="sm"
                leftIcon={ArrowPathIcon}
                onClick={() => setPendingAction('reenviar')}
                disabled={actionLoading || isLoading}
                isLoading={actionLoading}
              >
                Reintentar
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        title="Reintentar envío a Flexy"
        message="¿Deseas reintentar el envío de esta transferencia a Flexy?"
        type="warning"
        showCancel
        confirmText="Confirmar"
        onConfirm={handleConfirm}
      />
    </>
  )
}

export default FlexyTrasladoDetailModal
